import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Card,
  Table,
  Button,
  DatePicker,
  Select,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Empty,
} from "antd";
import {
  DownloadOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../services/api";
import LoadingSpinner from "../components/UI/LoadingSpinner";

const { Title } = Typography;
const { Option } = Select;

const Reports = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);
  const [guards, setGuards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [filters, setFilters] = useState({
    checkpoint_id: null,
    guard_id: null,
    status: null,
  });

  useEffect(() => {
    if (user?.current_company_id) {
      fetchData();
    }
  }, [user?.current_company_id]); // Re-fetch when company changes

  useEffect(() => {
    fetchReports();
  }, [selectedDate, filters]);

  const fetchData = async () => {
    try {
      const [checkpointsRes, statsRes] = await Promise.all([
        api.get("/checkpoints/list"), // Use read-only endpoint for Supervisor
        api.get("/reports/stats"),
      ]);

      if (checkpointsRes.data.success) {
        setCheckpoints(checkpointsRes.data.data);
      }

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
        // Extract guards from stats
        if (statsRes.data.data.by_guard) {
          setGuards(statsRes.data.data.by_guard);
        }
      }
    } catch (error) {
      console.error("Fetch data error:", error);
      message.error("ไม่สามารถโหลดข้อมูลได้");
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Add date range parameter (single day)
      const dateStr = selectedDate.format("YYYY-MM-DD");
      params.append("start_date", dateStr);
      params.append("end_date", dateStr);

      // Add other filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const response = await api.get(`/reports?${params.toString()}`);

      if (response.data.success) {
        setReports(response.data.data.logs);
      }
    } catch (error) {
      console.error("Fetch reports error:", error);
      message.error("ไม่สามารถโหลดรายงานได้");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const exportReport = async (format) => {
    try {
      setExporting(true);
      const params = new URLSearchParams();

      // Add date range parameter (single day)
      const dateStr = selectedDate.format("YYYY-MM-DD");
      params.append("start_date", dateStr);
      params.append("end_date", dateStr);

      // Add other filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `/reports/export/${format}?${params.toString()}`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type:
          format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `patrol-report-${selectedDate.format("YYYY-MM-DD")}.${format === "excel" ? "xlsx" : "pdf"
        }`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(
        `ส่งออกรายงานรูปแบบ ${format.toUpperCase()} สำเร็จ!`
      );
    } catch (error) {
      console.error("Export error:", error);
      message.error(`ไม่สามารถส่งออกรายงานรูปแบบ ${format} ได้`);
    } finally {
      setExporting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "missed":
        return <CloseCircleOutlined style={{ color: "#f5222d" }} />;
      case "unable":
        return <ExclamationCircleOutlined style={{ color: "#faad14" }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "green";
      case "missed":
        return "red";
      case "unable":
        return "orange";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "เวลาที่กำหนด",
      dataIndex: "time_scheduled",
      key: "time_scheduled",
      render: (date) => dayjs(date).format("YYYY-MM-DD HH:mm"),
      sorter: (a, b) => new Date(a.time_scheduled) - new Date(b.time_scheduled),
    },
    {
      title: "จุดตรวจ",
      dataIndex: "checkpoint_name",
      key: "checkpoint_name",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "รปภ.",
      dataIndex: "guard_name",
      key: "guard_name",
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: "สำเร็จ", value: "completed" },
        { text: "ขาดตรวจ", value: "missed" },
        { text: "ทำไม่ได้", value: "unable" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "เวลาตรวจจริง",
      dataIndex: "time_checked",
      key: "time_checked",
      render: (date) => (date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "-"),
    },
    {
      title: "เหตุผล",
      dataIndex: "reason",
      key: "reason",
      render: (reason) => reason || "-",
    },
    {
      title: "รูปภาพ",
      dataIndex: "image_count",
      key: "image_count",
      render: (count) => (count > 0 ? `${count} รูปภาพ` : "-"),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <FileTextOutlined /> รายงานและสถิติ
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => exportReport("excel")}
              loading={exporting}
            >
              ส่งออก Excel
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => exportReport("pdf")}
              loading={exporting}
            >
              ส่งออก PDF
            </Button>
          </Space>
        </Col>
      </Row>

      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stats-card">
              <Statistic
                title="จำนวนการตรวจทั้งหมด"
                value={stats.overall?.total_checks || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stats-card">
              <Statistic
                title="สำเร็จ"
                value={stats.overall?.completed_checks || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stats-card">
              <Statistic
                title="จำนวนที่ขาด"
                value={stats.overall?.missed_checks || 0}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: "#f5222d" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stats-card">
              <Statistic
                title="อัตราความสำเร็จ"
                value={stats.overall?.completion_rate || 0}
                suffix="%"
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{
                  color:
                    (stats.overall?.completion_rate || 0) >= 90
                      ? "#52c41a"
                      : (stats.overall?.completion_rate || 0) >= 70
                        ? "#faad14"
                        : "#f5222d",
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card title="ตัวกรอง" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} lg={8}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div style={{ fontWeight: 500, marginBottom: "8px" }}>วันที่</div>
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                style={{ width: "100%" }}
                placeholder="เลือกวันที่"
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) => {
                  return current && current > dayjs().endOf("day");
                }}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div style={{ fontWeight: 500, marginBottom: "8px" }}>
                จุดตรวจ
              </div>
              <Select
                placeholder="จุดตรวจทั้งหมด"
                value={filters.checkpoint_id}
                onChange={(value) => handleFilterChange("checkpoint_id", value)}
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {checkpoints.map((checkpoint) => (
                  <Option key={checkpoint.id} value={checkpoint.id}>
                    {checkpoint.name}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div style={{ fontWeight: 500, marginBottom: "8px" }}>รปภ.</div>
              <Select
                placeholder="รปภ.ทั้งหมด"
                value={filters.guard_id}
                onChange={(value) => handleFilterChange("guard_id", value)}
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {guards.map((guard, index) => (
                  <Option key={guard.guard_id || guard.id || index} value={guard.guard_id || guard.id}>
                    {guard.guard_name || guard.name}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div style={{ fontWeight: 500, marginBottom: "8px" }}>
                สถานะและการจัดการ
              </div>
              <Space.Compact style={{ width: "100%" }}>
                <Select
                  placeholder="ทุกสถานะ"
                  value={filters.status}
                  onChange={(value) => handleFilterChange("status", value)}
                  style={{ flex: 1 }}
                  allowClear
                >
                  <Option value="completed">✅ สำเร็จ</Option>
                  <Option value="missed">❌ ขาดตรวจ</Option>
                  <Option value="unable">⚠️ ทำไม่ได้</Option>
                </Select>
                <Button
                  onClick={() => {
                    setSelectedDate(dayjs());
                    setFilters({
                      checkpoint_id: null,
                      guard_id: null,
                      status: null,
                    });
                  }}
                  title="รีเซ็ตตัวกรอง"
                >
                  ล้างค่า
                </Button>
              </Space.Compact>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        {loading ? (
          <LoadingSpinner tip="กำลังโหลดรายงาน..." />
        ) : reports.length > 0 ? (
          <Table
            columns={columns}
            dataSource={reports}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} จากทั้งหมด ${total} รายการ`,
            }}
            scroll={{ x: 1000 }}
          />
        ) : (
          <Empty
            description="ไม่พบรายงานตามเงื่อนไขที่เลือก"
            style={{ margin: "40px 0" }}
          />
        )}
      </Card>
    </div>
  );
};

export default Reports;
